import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { HelmetProvider } from "react-helmet-async";

// Pages
import Home2027 from "./pages/Home2027";
import PreInscricaoPage from "./pages/PreInscricaoPage";
import PreInscricoesPage from "./pages/admin/PreInscricoesPage";
import MemoriasPage from "./pages/MemoriasPage";
import GaleriaPage from "./pages/admin/GaleriaPage";
import ContatoPage from "./pages/ContatoPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import MinhaInscricaoPage from "./pages/MinhaInscricaoPage";
import AuthCallback from "./pages/AuthCallback";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/admin/DashboardPage";
import EventosPage from "./pages/admin/EventosPage";
import LotesPage from "./pages/admin/LotesPage";
import ParticipantesPage from "./pages/admin/ParticipantesPage";
import PagamentosPage from "./pages/admin/PagamentosPage";
import CheckinPage from "./pages/admin/CheckinPage";
import ConfiguracoesPage from "./pages/admin/ConfiguracoesPage";
import MercadoPagoLogsPage from "./pages/admin/MercadoPagoLogsPage";
import CadastroManualPage from "./pages/admin/CadastroManualPage";
import ParticipantesImprimirPage from "./pages/admin/ParticipantesImprimirPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TermosCondicoesPage from "./pages/TermosCondicoesPage";
import TermoCompromissoPage from "./pages/TermoCompromissoPage";
import PoliticaPrivacidadePage from "./pages/PoliticaPrivacidadePage";
import PagamentoSucessoPage from "./pages/PagamentoSucessoPage";
import PagamentoPendentePage from "./pages/PagamentoPendentePage";
import PagamentoErroPage from "./pages/PagamentoErroPage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id BEFORE rendering routes
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes - edição 2027 */}
      <Route path="/" element={<Home2027 />} />
      <Route path="/pre-inscricao" element={<PreInscricaoPage />} />
      <Route path="/memorias" element={<MemoriasPage />} />
      <Route path="/memorias/:albumId" element={<MemoriasPage />} />
      <Route path="/galeria" element={<Navigate to="/memorias" replace />} />
      <Route path="/contato" element={<ContatoPage />} />
      <Route path="/minha-inscricao" element={<MinhaInscricaoPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />

      {/* Rotas da edição 2026 (programação, palestrantes, checkout) -> redirecionadas enquanto o lote 2027 não abre */}
      <Route path="/programacao" element={<Navigate to="/" replace />} />
      <Route path="/local" element={<Navigate to="/" replace />} />
      <Route path="/o-que-inclui" element={<Navigate to="/" replace />} />
      <Route path="/evento/:eventoId" element={<Navigate to="/" replace />} />
      <Route path="/inscricao" element={<Navigate to="/pre-inscricao" replace />} />
      <Route path="/inscricao/:eventoId" element={<Navigate to="/pre-inscricao" replace />} />
      <Route path="/checkout/:participanteId" element={<Navigate to="/pre-inscricao" replace />} />
      <Route path="/lista-espera/:eventoId" element={<Navigate to="/pre-inscricao" replace />} />
      
      {/* Legal Pages - Public */}
      <Route path="/termos-e-condicoes" element={<TermosCondicoesPage />} />
      <Route path="/termo-de-compromisso" element={<TermoCompromissoPage />} />
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidadePage />} />
      
      {/* Payment Return Pages - Mercado Pago */}
      <Route path="/pagamento/sucesso" element={<PagamentoSucessoPage />} />
      <Route path="/pagamento/pendente" element={<PagamentoPendentePage />} />
      <Route path="/pagamento/erro" element={<PagamentoErroPage />} />
      
      {/* Auth Routes */}
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
      <Route path="/admin/callback" element={<AuthCallback />} />
      
      {/* Admin Protected Routes */}
      <Route path="/admin" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin/pre-inscricoes" element={<ProtectedRoute><PreInscricoesPage /></ProtectedRoute>} />
      <Route path="/admin/galeria" element={<ProtectedRoute><GaleriaPage /></ProtectedRoute>} />
      <Route path="/admin/galeria/:albumId" element={<ProtectedRoute><GaleriaPage /></ProtectedRoute>} />
      <Route path="/admin/eventos" element={<ProtectedRoute><EventosPage /></ProtectedRoute>} />
      <Route path="/admin/lotes" element={<ProtectedRoute><LotesPage /></ProtectedRoute>} />
      <Route path="/admin/participantes" element={<ProtectedRoute><ParticipantesPage /></ProtectedRoute>} />
      <Route path="/admin/participantes/cadastro-manual" element={<ProtectedRoute><CadastroManualPage /></ProtectedRoute>} />
      <Route path="/admin/participantes/imprimir" element={<ProtectedRoute><ParticipantesImprimirPage /></ProtectedRoute>} />
      <Route path="/admin/pagamentos" element={<ProtectedRoute><PagamentosPage /></ProtectedRoute>} />
      <Route path="/admin/checkin" element={<ProtectedRoute><CheckinPage /></ProtectedRoute>} />
      <Route path="/admin/configuracoes" element={<ProtectedRoute><ConfiguracoesPage /></ProtectedRoute>} />
      <Route path="/admin/mercadopago-logs" element={<ProtectedRoute><MercadoPagoLogsPage /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
